import { type AiTagId } from '@solinkcorp/api-types';
import { z } from 'zod';

// Base common fields for all SDM events
const BaseSdmEventSchema = z.object({
  port: z.union([z.number(), z.string()]),
  cam_id: z.string(),
  stream_id: z.string(),
  evt_id: z.number(),
  evt_type: z.enum(['zone', 'line']),
  aiTagIds: z.array(z.string()).min(1),
});

// Zone-specific fields
const ZoneEventSchema = BaseSdmEventSchema.extend({
  evt_type: z.literal('zone'),
  object_id: z.number(),
  object_kind: z.string(),
  polygon: z.object({
    vertices: z.array(z.array(z.number()).length(2)).min(3), // At least 3 vertices for a polygon
  }),
  min_threshold: z.number(),
  label: z.string(),
  subtype: z.enum(['dwell', 'exit']),
  entry_ms: z.number(),
  exit_ms: z.number().nullable(),
  last_update_ms: z.number().nullable(),
  drive_off: z.boolean().nullable().default(null),
  vehicle_journey: z
    .object({
      start_zone_entry_time: z.number(),
      start_zone_exit_time: z.number(),
      /**
       * Total journey duration in MILLISECONDS — the edge computes it as the end-zone
       * `exit_ms` minus `start_zone_entry_time`.
       */
      total_vehicle_time: z.number(),
      /** `object_id` the vehicle was tracked under in the start zone. */
      start_zone_obj_id: z.number(),
    })
    .passthrough()
    .nullable()
    .optional(),
  metadata: z
    .object({
      zoneType: z.string(),
      polygonId: z.string(),
      aiTagIds: z.array(z.string()),
    })
    .passthrough(),
});

// Line event subtypes, mirroring the connect-ai `line_crossing` module's event kinds.
export const LINE_EVENT_SUBTYPES = [
  'entrance_and_exit',
  'passerby_traffic',
  'floor_level_transition',
] as const;
export type LineEventSubtype = (typeof LINE_EVENT_SUBTYPES)[number];

// Line-specific fields
const LineEventSchema = BaseSdmEventSchema.extend({
  evt_type: z.literal('line'),
  start_ms: z.number(),
  end_ms: z.number(),
  event_line: z.object({
    line: z.array(z.number()).length(4), // [x1, y1, x2, y2]
    dir_ref: z.array(z.number()).length(2), // [x, y] direction reference
    debounce_ms: z.number(),
    min_bounce_ms: z.number(),
    label: z.string(),
    neg_label: z.string(),
    pos_label: z.string(),
    subtype: z.enum(LINE_EVENT_SUBTYPES).optional(),
    metadata: z
      .object({
        aiTagIds: z.array(z.string()),
      })
      .passthrough(), // Allow additional fields to prevent production failures
    object_kinds: z.array(z.string()),
    batched_kinds: z.array(z.string()),
    batch_size: z.number(),
    batch_timeout_ms: z.number(),
  }),
  intersections: z.array(
    z.object({
      id: z.number(),
      object_id: z.number(),
      t: z.number(),
      dir: z.number(),
      object_kind: z.string(),
      path: z.object({
        points: z.array(
          z.object({
            x: z.number(),
            y: z.number(),
            w: z.number(),
            h: z.number(),
            t: z.number(),
          }),
        ),
      }),
      evt_id: z.number(),
    }),
  ),
  bounces: z.array(z.unknown()), // Empty in the example, but keeping flexible
});

// Union type for all SDM events
export const SdmEventSchema = z.discriminatedUnion('evt_type', [ZoneEventSchema, LineEventSchema]);

// TypeScript types derived from Zod schemas
export type ZoneEvent = z.infer<typeof ZoneEventSchema>;
export type LineEvent = z.infer<typeof LineEventSchema>;
export type SdmEvent = z.infer<typeof SdmEventSchema>;

// Type guards
export const isZoneEvent = (event: SdmEvent): event is ZoneEvent => {
  return event.evt_type === 'zone';
};

export const isLineEvent = (event: SdmEvent): event is LineEvent => {
  return event.evt_type === 'line';
};

/**
 * Latency (ms) for an event as it drives the SDM widgets, measured as `receiptMs − <the
 * video-timeline moment the event reflects>`:
 *  - Zone → `exit_ms` on an exit, else `entry_ms`.
 *  - Line crossing → `end_ms`, unless the crossing is batched (its `object_kind` is in
 *    `event_line.batched_kinds`), in which case it arrives in ~10-min batches and isn't a
 *    real-time signal — returns `null` so the caller skips it.
 */
export const getWidgetLatencyMs = (event: SdmEvent, receiptMs: number): number | null => {
  if (isLineEvent(event)) {
    // interlink-ai emits exactly one intersection per real-time line event (batched
    // multi-crossing events are excluded just below), so reading [0] is sufficient.
    const crossing = event.intersections[0];
    if (!crossing || event.event_line.batched_kinds.includes(crossing.object_kind)) {
      return null;
    }
    return Math.max(0, receiptMs - event.end_ms);
  }
  return Math.max(0, receiptMs - (event.exit_ms ?? event.entry_ms));
};

// Validation function
export const validateSdmEvent = (data: unknown): SdmEvent => {
  return SdmEventSchema.parse(data);
};

// Safe validation function that returns a result
export const safeValidateSdmEvent = (
  data: unknown,
): { success: true; data: SdmEvent } | { success: false; error: z.ZodError } => {
  const result = SdmEventSchema.safeParse(data);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
};

// Helper types for the controller
export type EventProcessingResult = {
  success: boolean;
  eventIds?: string[];
  aiTagIds?: AiTagId[];
  error?: string;
};

// Constants
export const SDM_EVENT_TYPES = {
  ZONE: 'zone' as const,
  LINE: 'line' as const,
} as const;

export const ZONE_SUBTYPES = {
  DWELL: 'dwell' as const,
  EXIT: 'exit' as const,
} as const;
